import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { AlertCircle, ChevronDown, Home } from 'lucide-react';
import { 
  WATCH_LATER_SIZES, 
  PREDEFINED_PLATFORMS, 
  PREDEFINED_USE_CASES, 
  PREDEFINED_ROLES,
  WatchLaterSize 
} from '../types/waitlist';
import { waitlistAPI } from '../services/waitlistApi';
import Footer from './Footer';

interface QuestionsPageProps {
  onTermsClick: () => void;
  onPrivacyClick: () => void;
  onBack?: () => void;
}

interface QuestionsFormData {
  whatWouldYouQuellect?: string;
  nextPlatforms: string[];
  primaryUseCase?: string;
  role?: string;
  watchLaterSize?: WatchLaterSize;
  otherThoughts?: string;
}

const QuestionsPage: React.FC<QuestionsPageProps> = ({ onTermsClick, onPrivacyClick, onBack }) => {
  const [email, setEmail] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [isVerified, setIsVerified] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [otherPlatform, setOtherPlatform] = useState('');
  const [otherUseCase, setOtherUseCase] = useState('');
  const [otherRole, setOtherRole] = useState('');
  const [apiError, setApiError] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successType, setSuccessType] = useState<'completed' | 'already_submitted'>('completed');

  const {
    register: registerQuestions,
    handleSubmit: handleSubmitQuestions,
    watch: watchQuestions,
    setValue: setQuestionsValue,
    formState: { errors: questionErrors },
  } = useForm<QuestionsFormData>({
    defaultValues: {
      nextPlatforms: [],
    },
  });

  const watchedQuestions = watchQuestions();

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.custom-dropdown')) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Questions configuration with proper validation constants
  const questions = [
    {
      id: 'watchLaterSize',
      label: 'Honestly, how long is your Save for Later list? (YouTube, Instagram, TikTok, etc.)',
      type: 'radio',
      options: WATCH_LATER_SIZES,
      helpText: 'No judgment—we\'ve all been there'
    },
    {
      id: 'whatWouldYouQuellect',
      label: 'What would you like to Quellect?',
      type: 'textarea',
      placeholder: 'e.g., AI tutorials, cooking videos, business podcasts...',
      helpText: 'Tell us about the content you want to collect'
    },
    {
      id: 'nextPlatforms',
      label: 'Which platforms would you like us to support next?',
      type: 'checkbox',
      options: [...PREDEFINED_PLATFORMS, 'Other'],
      helpText: 'Select all that apply'
    },
    {
      id: 'primaryUseCase',
      label: 'What\'s your primary use case?',
      type: 'radio',
      options: [...PREDEFINED_USE_CASES, 'Other'],
      helpText: 'Choose the most relevant option'
    },
    {
      id: 'role',
      label: 'What\'s your role?',
      type: 'select',
      options: [...PREDEFINED_ROLES, 'Other'],
      helpText: 'This helps us personalize your experience'
    },
    {
      id: 'otherThoughts',
      label: 'Anything else you want to share?',
      type: 'textarea',
      placeholder: 'Yes, I was thinking...',
      helpText: 'Share any additional thoughts, feedback, or ideas...'
    }
  ];

  const totalQuestions = questions.length;
  const questionsProgress = (currentQuestionIndex / totalQuestions) * 100;

  // Navigation functions
  const goToNextQuestion = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const canGoNext = () => {
    const currentQuestion = questions[currentQuestionIndex];
    
    // Question 1: watchLaterSize (radio)
    if (currentQuestion.id === 'watchLaterSize') {
      const result = watchedQuestions.watchLaterSize;
      return result;
    }
    
    // Question 2: whatWouldYouQuellect (textarea)
    if (currentQuestion.id === 'whatWouldYouQuellect') {
      const result = watchedQuestions.whatWouldYouQuellect?.trim();
      return result;
    }
    
    // Question 3: nextPlatforms (checkbox)
    if (currentQuestion.id === 'nextPlatforms') {
      const hasSelection = watchedQuestions.nextPlatforms?.length > 0;
      
      // If "Other" is selected, ensure the input field is filled
      if (hasSelection && watchedQuestions.nextPlatforms?.includes('Other')) {
        const result = otherPlatform.trim().length > 0;
        return result;
      }
      return hasSelection;
    }
    
    // Question 4: primaryUseCase (radio)
    if (currentQuestion.id === 'primaryUseCase') {
      const hasSelection = watchedQuestions.primaryUseCase;
      
      // If "Other" is selected, ensure the input field is filled
      if (hasSelection === 'Other') {
        const result = otherUseCase.trim().length > 0;
        return result;
      }
      return hasSelection;
    }
    
    // Question 5: role (select)
    if (currentQuestion.id === 'role') {
      const hasSelection = watchedQuestions.role;
      
      // If "Other" is selected, ensure the input field is filled
      if (hasSelection === 'Other') {
        const result = otherRole.trim().length > 0;
        return result;
      }
      return hasSelection;
    }
    
    // Question 6: otherThoughts (textarea) - required
    if (currentQuestion.id === 'otherThoughts') {
      return true;
    }
    return false;
  };

  const handleMultiSelectChange = (value: string, checked: boolean) => {
    const currentValues = watchedQuestions.nextPlatforms || [];
    if (checked) {
      setQuestionsValue('nextPlatforms', [...currentValues, value]);
    } else {
      setQuestionsValue('nextPlatforms', currentValues.filter(v => v !== value));
    }
  };

  const onQuestionsSubmit = async (data: QuestionsFormData) => {
    if (isLoading) return;
    
    setApiError(null);
    setIsLoading(true);
    
    try {
      // If "Other" is selected, replace it with the actual input value
      let processedData = { ...data };
      
      // Handle nextPlatforms "Other"
      if (data.nextPlatforms?.includes('Other') && otherPlatform.trim()) {
        processedData.nextPlatforms = data.nextPlatforms
          .filter(platform => platform !== 'Other')
          .concat(otherPlatform.trim());
      }
      
      // Handle primaryUseCase "Other"
      if (data.primaryUseCase === 'Other' && otherUseCase.trim()) {
        processedData.primaryUseCase = otherUseCase.trim();
      }
      
      // Handle role "Other"
      if (data.role === 'Other' && otherRole.trim()) {
        processedData.role = otherRole.trim();
      }
      
      // Validate required fields
      if (!processedData.watchLaterSize || !processedData.whatWouldYouQuellect || 
          !processedData.nextPlatforms || !processedData.primaryUseCase || !processedData.role) {
        setApiError('Please fill in all required fields');
        return;
      }
      
      // Submit questions to FastAPI backend
      const response = await waitlistAPI.submitQuestions({
        watchLaterSize: processedData.watchLaterSize,
        whatWouldYouQuellect: processedData.whatWouldYouQuellect,
        nextPlatforms: processedData.nextPlatforms,
        primaryUseCase: processedData.primaryUseCase,
        role: processedData.role,
        otherThoughts: processedData.otherThoughts || ''
      });
      
      if (response.success && response.data) {
        setSuccessType('completed');
        setShowSuccess(true);
      } else {
        setApiError(response.message || 'Questions submission failed');
      }
    } catch (error) {
      setApiError(error instanceof Error ? error.message : 'Questions submission failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    setIsVerifying(true);
    setVerificationError(null);
    
    try {
      const response = await waitlistAPI.verifyEmailSubscription(email.trim());
      
      if (response.success && response.data) {
        // Check if questions were already submitted
        if (response.data.has_submitted_questions) {
          // Show the "already submitted" message
          setSuccessType('already_submitted');
          setShowSuccess(true);
        } else {
          // Proceed to questions form
          setIsVerified(true);
        }
        
        setVerificationError(null);
      } else {
        setVerificationError(response.message || 'Email not found in waitlist');
      }
    } catch (error) {
      setVerificationError('Failed to verify email. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleBackClick = () => {
    if (onBack) {
      onBack();
    } else {
      window.history.back();
    }
  };

  // Show success screen if questions are already completed
  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-900 flex flex-col p-4 relative no-overscroll" style={{position: 'relative', zIndex: 9999}}>
        <div className="flex-1 flex items-center justify-center mt-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, type: "spring" }}
            className="max-w-2xl w-full"
          >
            <div className="card text-center p-12">
              <div className="w-24 h-24 bg-green-100 rounded-full mx-auto mb-6 flex items-center justify-center">
                <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                {successType === 'completed' ? 'Thank You! 🎉' : 'Questions Already Submitted! ✅'}
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                {successType === 'completed' 
                  ? "Your responses have been submitted successfully. We'll use this information to make Quellect perfect for you!"
                  : "You've already completed the questions for this email address. We'll use your responses to make Quellect perfect for you!"
                }
              </p>
                <button
                  onClick={() => window.location.href = '/'}
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
                >
                  <Home className="w-5 h-5" />
                  <span>Go to Homepage</span>
                </button>
            </div>
          </motion.div>
        </div>
        
        {/* Footer */}
        <Footer onTermsClick={onTermsClick} onPrivacyClick={onPrivacyClick} />
      </div>
    );
  }

  // Show email input if not verified
  if (!isVerified) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-900 flex flex-col p-4 relative no-overscroll" style={{position: 'relative', zIndex: 9999}}>
        <div className="flex-1 flex items-center justify-center mt-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, type: "spring" }}
            className="max-w-2xl w-full"
          >
            <div className="card text-center p-12">
              <div className="w-24 h-24 bg-primary-100 rounded-full mx-auto mb-6 flex items-center justify-center">
                <svg className="w-12 h-12 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Enter Your Email</h1>
              <p className="text-lg text-gray-600 mb-8">
                Please enter the email address you used to join the waitlist to access the questions
              </p>
              
              <form onSubmit={handleEmailSubmit} className="max-w-md mx-auto">
                <div className="mb-6">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="form-input w-full text-center text-lg"
                    disabled={isVerifying}
                    required
                  />
                </div>
                
                {verificationError && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="text-red-700 text-sm">
                      {verificationError}
                    </div>
                  </div>
                )}
                
                <button
                  type="submit"
                  disabled={!email.trim() || isVerifying}
                  className={`w-full btn-primary ${
                    !email.trim() || isVerifying
                      ? 'opacity-50 cursor-not-allowed transform-none'
                      : ''
                  }`}
                >
                  {isVerifying ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Verifying...</span>
                    </div>
                  ) : (
                    'Verify & Continue'
                  )}
                </button>
              </form>
              
              <div className="mt-8">
                <button
                  onClick={handleBackClick}
                  className="text-primary-600 hover:text-primary-700 underline"
                >
                  Go Back
                </button>
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Footer */}
        <Footer onTermsClick={onTermsClick} onPrivacyClick={onPrivacyClick} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-900 flex flex-col p-4 relative" style={{position: 'relative', zIndex: 9999}}>
      <div className="flex-1 flex items-center justify-center mt-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, type: "spring" }}
          className="max-w-4xl w-full"
        >
          {/* Main Content Card */}
          <div className="card max-w-5xl mx-auto min-h-[550px]">
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="w-20 h-20 rounded-2xl mx-auto mb-8 flex items-center justify-center"
            >
              <img src="/logo.svg" alt="Quellect" className="w-16 h-16 drop-shadow-lg" />
            </motion.div>

            {/* Questions Header */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Help us personalize your experience
              </h2>
              <p className="text-gray-600">
                A few quick questions to make Quellect perfect for you
              </p>
            </div>

            {/* Progress Bar */}
            <div className="mb-8 max-w-2xl mx-auto">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Question {currentQuestionIndex + 1} of {totalQuestions}</span>
                <span className="text-sm text-gray-500">{Math.round(questionsProgress)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <motion.div
                  className="bg-gradient-to-r from-primary-500 to-secondary-500 h-2 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${questionsProgress}%` }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                />
              </div>
            </div>

            {/* API Error Display */}
            {apiError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center space-x-2 text-red-700">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">{apiError}</span>
                </div>
              </div>
            )}

            {/* Current Question */}
            <form onSubmit={(e) => {
              // Only allow form submission on the last question
              if (currentQuestionIndex < totalQuestions - 1) {
                e.preventDefault();
                goToNextQuestion();
              } else {
                handleSubmitQuestions(onQuestionsSubmit)(e);
              }
            }} className="max-w-lg mx-auto">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentQuestionIndex}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  {/* Question Label */}
                  <div className="text-center">
                    <label className="form-label text-lg">
                      {questions[currentQuestionIndex].id === 'watchLaterSize' ? (
                        <>
                          <div>Honestly, how long is your Save for Later list?</div>
                          <div className="text-sm text-gray-600 mt-1">(YouTube, Instagram, TikTok, etc.)</div>
                        </>
                      ) : (
                        questions[currentQuestionIndex].label
                      )}
                    </label>
                    <p className="mt-2 text-sm text-gray-500">
                      {questions[currentQuestionIndex].helpText}
                    </p>
                  </div>

                  {/* Question Input */}
                  <div className="flex justify-center">
                    <div className="w-full max-w-md">
                      {questions[currentQuestionIndex].type === 'textarea' && (
                        <textarea
                          {...registerQuestions(questions[currentQuestionIndex].id as keyof QuestionsFormData)}
                          placeholder={questions[currentQuestionIndex].placeholder}
                          className="form-input min-h-[80px] resize-none"
                          disabled={isLoading}
                        />
                      )}

                      {questions[currentQuestionIndex].type === 'checkbox' && (
                        <div className="space-y-3">
                          {questions[currentQuestionIndex].options?.map((option) => (
                            <label key={option} className="flex items-center space-x-3 cursor-pointer justify-start p-2 rounded-3xl hover:bg-gray-50 transition-colors duration-200 group border border-gray-200 hover:border-primary-200">
                              <div className="relative ml-1.5">
                                <input
                                  type="checkbox"
                                  checked={watchedQuestions.nextPlatforms?.includes(option) || false}
                                  onChange={(e) => handleMultiSelectChange(option, e.target.checked)}
                                  className={`h-5 w-5 focus:ring-primary-500 border-gray-300 rounded-lg transition-all duration-200 group-hover:border-primary-400 appearance-none mt-1.5 ${
                                    watchedQuestions.nextPlatforms?.includes(option) 
                                      ? 'bg-primary-600 border-primary-600' 
                                      : 'bg-white border-gray-300'
                                  }`}
                                  disabled={isLoading}
                                />
                                {watchedQuestions.nextPlatforms?.includes(option) && (
                                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20" strokeWidth="5">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                              <span className="text-gray-700 font-medium group-hover:text-gray-900 transition-colors duration-200">{option}</span>
                            </label>
                          ))}
                          
                          {/* Other input field for nextPlatforms - only show when Other is checked */}
                          {watchedQuestions.nextPlatforms?.includes('Other') && (
                            <div className="ml-7">
                              <input
                                type="text"
                                value={otherPlatform}
                                onChange={(e) => setOtherPlatform(e.target.value)}
                                placeholder="Please specify platform..."
                                className="form-input w-full"
                                disabled={isLoading}
                                required={watchedQuestions.nextPlatforms?.includes('Other')}
                              />
                            </div>
                          )}
                          
                        </div>
                      )}

                      {questions[currentQuestionIndex].type === 'radio' && (
                        <div className="space-y-3">
                          {questions[currentQuestionIndex].options?.map((option) => (
                            <label key={option} className="flex items-center space-x-3 cursor-pointer justify-start p-2 rounded-3xl hover:bg-gray-50 transition-colors duration-200 group border border-gray-200 hover:border-primary-200">
                              <div className="relative ml-1.5">
                                <input
                                  type="radio"
                                  value={option}
                                  {...registerQuestions(questions[currentQuestionIndex].id as keyof QuestionsFormData)}
                                  className={`h-5 w-5 focus:ring-primary-500 border-gray-300 rounded-full transition-all duration-200 group-hover:border-primary-400 appearance-none mt-1.5 ${
                                    (questions[currentQuestionIndex].id === 'primaryUseCase' && watchedQuestions.primaryUseCase === option) ||
                                    (questions[currentQuestionIndex].id === 'watchLaterSize' && watchedQuestions.watchLaterSize === option)
                                      ? 'bg-primary-600 border-primary-600' 
                                      : 'bg-white border-gray-300'
                                  }`}
                                  disabled={isLoading}
                                />
                                {(questions[currentQuestionIndex].id === 'primaryUseCase' && watchedQuestions.primaryUseCase === option) ||
                                 (questions[currentQuestionIndex].id === 'watchLaterSize' && watchedQuestions.watchLaterSize === option) ? (
                                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                  </div>
                                ) : null}
                              </div>
                              <span className="text-gray-700 font-medium group-hover:text-gray-900 transition-colors duration-200">{option}</span>
                            </label>
                          ))}
                          
                          {/* Other input field for radio questions - only show when Other is checked */}
                          {questions[currentQuestionIndex].id === 'primaryUseCase' && watchedQuestions.primaryUseCase === 'Other' && (
                            <div className="ml-7">
                              <input
                                type="text"
                                value={otherUseCase}
                                onChange={(e) => setOtherUseCase(e.target.value)}
                                placeholder="Please specify use case..."
                                className="form-input w-full"
                                disabled={isLoading}
                                required={watchedQuestions.primaryUseCase === 'Other'}
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {questions[currentQuestionIndex].type === 'select' && (
                        <div className="space-y-3 custom-dropdown">
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                              className="form-input w-full text-left flex items-center justify-between bg-white/90 backdrop-blur-sm px-4 py-3 border border-gray-300 rounded-3xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                              disabled={isLoading}
                            >
                              <span className={watchedQuestions.role ? 'text-gray-900' : 'text-gray-500'}>
                                {watchedQuestions.role || 'Select an option'}
                              </span>
                              <ChevronDown 
                                className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                                  isDropdownOpen ? 'rotate-180' : ''
                                }`} 
                              />
                            </button>
                            
                            {isDropdownOpen && (
                              <div className="absolute z-[100] w-full mt-1 bg-white border border-gray-200 rounded-3xl shadow-lg max-h-60 overflow-y-auto scrollbar-hide">
                                {questions[currentQuestionIndex].options?.map((option) => (
                                  <button
                                    key={option}
                                    type="button"
                                    onClick={() => {
                                      setQuestionsValue(questions[currentQuestionIndex].id as keyof QuestionsFormData, option);
                                      setIsDropdownOpen(false);
                                    }}
                                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors duration-150 ${
                                      watchedQuestions.role === option ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                                    }`}
                                  >
                                    {option}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          {/* Other input field for select questions - only show when Other is selected */}
                          {questions[currentQuestionIndex].id === 'role' && watchedQuestions.role === 'Other' && (
                            <div>
                              <input
                                type="text"
                                value={otherRole}
                                onChange={(e) => setOtherRole(e.target.value)}
                                placeholder="Please specify your role..."
                                className="form-input w-full"
                                disabled={isLoading}
                                required={watchedQuestions.role === 'Other'}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Navigation Buttons */}
              <div className="flex justify-between items-center mt-8">
                <button
                  type="button"
                  onClick={goToPreviousQuestion}
                  disabled={currentQuestionIndex === 0 || isLoading}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    currentQuestionIndex === 0 || isLoading
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-primary-600 hover:text-primary-700'
                  }`}
                >
                  ← Previous
                </button>

                {currentQuestionIndex < totalQuestions - 1 ? (
                  <button
                    type="submit"
                    disabled={!canGoNext() || isLoading}
                    className={`px-6 py-2 rounded-3xl transition-colors ${
                      !canGoNext() || isLoading
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-primary-600 text-white hover:bg-primary-700'
                    }`}
                  >
                    Next →
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={!canGoNext() || isLoading}
                    className={`px-6 py-2 rounded-3xl transition-colors ${
                      !canGoNext() || isLoading
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-primary-600 text-white hover:bg-primary-700'
                    }`}
                  >
                    {isLoading ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Submitting...</span>
                      </div>
                    ) : (
                      'Submit'
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <Footer onTermsClick={onTermsClick} onPrivacyClick={onPrivacyClick} />
    </div>
  );
};

export default QuestionsPage;
